/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { FC, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Stack, Typography } from '@mui/material';

import withTfoAccess from '../../../../util/withTfoAccess';
import { CostPrice } from '../../costprice.types';
import { getCostPrice, selectCostPriceById, selectCostPriceStatus } from '../../costprice.slice';
import { selectCurrentFarmerAccount } from '../../../azure-b2c/storage';
import { useActionRequest } from '../../../../hooks/useActionRequest';
import { useAppDispatch } from '../../../../store/rootStore';
import { RootState } from '../../../../store/rootReducer';
import CostPriceLoading from './CostPriceLoading';
import CostPriceError from './CostPriceError';
import CostPriceSuccess from './CostPriceSuccess';
import TestIds from '../../../../testIds';
import CostPriceEmpty from './CostPriceEmpty';
import { ExpandableContent } from '../../../../components/ExpandableContent/ExpandableContent';

export interface ICostPriceProps {
  productId: number;
  defaultExpanded?: boolean;
}

const CostPriceListComponent: FC<ICostPriceProps> = ({
  productId,
  defaultExpanded,
}: ICostPriceProps) => {
  const dispatch = useAppDispatch();
  const account = useSelector(selectCurrentFarmerAccount);
  const costPrices = useSelector(selectCostPriceById(productId));

  const fetchCostPrice = useMemo(() => {
    return async () => {
      if (account?.accountNumber) {
        await dispatch(getCostPrice({ productId, accountNumber: account.accountNumber }));
      }
    };
  }, [dispatch, productId, account?.accountNumber]);

  const statusSelector = useMemo(() => selectCostPriceStatus(productId), [productId]);

  const { isLoading, isSuccess, isError, isCancelled, triggerRequest } = useActionRequest<
    void,
    RootState
  >({
    action: fetchCostPrice,
    statusSelector,
    debounceMs: 200,
  });

  useEffect(() => {
    triggerRequest();
  }, [triggerRequest, productId, account?.accountNumber]);

  const processedCostPrices = useMemo(() => {
    if (!costPrices?.length) {
      return null;
    }

    const groupedBreaks: Record<string, CostPrice[]> = costPrices.reduce<
      Record<string, CostPrice[]>
    >((acc: Record<string, CostPrice[]>, curr: CostPrice) => {
      const siteKey = curr.site ?? 'Other';
      acc[siteKey] = acc[siteKey] || [];
      acc[siteKey].push(curr);
      return acc;
    }, {});

    Object.keys(groupedBreaks).forEach((site: string) => {
      groupedBreaks[site] = groupedBreaks[site].toSorted(
        (a: CostPrice, b: CostPrice) => a.quantityFrom - b.quantityFrom,
      );
    });

    return Object.entries(groupedBreaks).sort(
      (a: [string, CostPrice[]], b: [string, CostPrice[]]) => a[0].localeCompare(b[0]),
    );
  }, [costPrices]);

  const content = useMemo(() => {
    if (isLoading) {
      return <CostPriceLoading processedCostPrices={processedCostPrices} />;
    }

    if (isError || isCancelled) {
      return <CostPriceError onTryAgain={triggerRequest} />;
    }

    if (isSuccess && processedCostPrices) {
      return <CostPriceSuccess processedCostPrices={processedCostPrices} />;
    }

    return <CostPriceEmpty />;
  }, [isLoading, isSuccess, isError, isCancelled, processedCostPrices, triggerRequest]);

  return (
    <Stack spacing='1rem' alignItems='start' data-testid={TestIds.feat.costPrice.component}>
      <ExpandableContent
        title={<Typography variant='subtitle1'>Cost Price</Typography>}
        defaultExpanded={defaultExpanded}
      >
        {content}
      </ExpandableContent>
    </Stack>
  );
};

export const CostPriceList = withTfoAccess(CostPriceListComponent);
